// Generated by DesignToCode

import {
  Component,
  output,
  input,
  ElementRef,
  signal,
  computed,
  PLATFORM_ID,
  Inject,
  effect,
  viewChild,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Dropdown as FlowbiteDropdown, DropdownInterface, DropdownOptions } from 'flowbite';
import { SearchField } from '../search-field/search-field';
import { DropdownItem } from '../dropdown-item/dropdown-item';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { GenericInputField } from '../generic-input-field/generic-input-field';

export interface DropdownOption<T = unknown> {
  id: number;
  label: string;
  value: T;
  icon?: string;
  type: 'default' | 'checkbox' | 'radio';
}

export interface DropdownSection<T = unknown> {
  title?: string;
  options: DropdownOption<T>[];
}

@Component({
  selector: 'app-dropdown',
  standalone: true,
  imports: [CommonModule, SearchField, DropdownItem, GenericInputField],
  templateUrl: './dropdown.html',
  styles: [
    `
      :host {
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class Dropdown<T> implements OnInit {
  public dropdownTrigger = viewChild<ElementRef>('dropdownTrigger');
  public dropdownTargetRef = viewChild<ElementRef>('dropdownTarget');

  // Inputs
  public select = output<DropdownOption<T>>();
  public sections = input.required<DropdownSection<T>[]>();
  public formGroup = input.required<FormGroup>();
  public controlName = input.required<string>();
  public placeholder = input<string>('Select an option');
  public disabled = input<boolean>(false);
  public size = input<'small' | 'medium' | 'large'>('medium');
  public type = input<'default' | 'search-bar'>('default');

  // State
  private destroy$ = new Subject<void>();
  public dropdown: DropdownInterface | undefined;
  public selected = signal<DropdownOption<T> | null>(null);
  private _searchTerm = signal<string>('');
  public dropdownExpanded = signal<boolean>(false);
  private controlDisabled = signal<boolean>(false);

  public isDisabled = computed(() => this.disabled() || this.controlDisabled());

  public dropdownForm = new FormGroup({
    dropdownFormControl: new FormControl<unknown>(null),
  });

  private flowbiteOptions: DropdownOptions = {
    placement: 'bottom-start',
    triggerType: 'click',
    ignoreClickOutsideClass: false,
    delay: 300,
    onHide: () => {
      this.dropdownExpanded.set(false);
      if (this.dropdownForm.controls.dropdownFormControl && !this.selected()) {
        this.dropdownForm.controls.dropdownFormControl.setValue(null, { emitEvent: false });
        this.dropdownForm.controls.dropdownFormControl.markAsDirty();
        this.dropdownForm.controls.dropdownFormControl.markAsTouched();
      } else if (this.dropdownForm.controls.dropdownFormControl && this.selected()) {
        this.dropdownForm.controls.dropdownFormControl.setValue(this.selected()?.label, {
          emitEvent: false,
        });
        this.dropdownForm.controls.dropdownFormControl.markAsDirty();
        this.dropdownForm.controls.dropdownFormControl.markAsTouched();
      }
    },
    onShow: () => {
      this.dropdownExpanded.set(true);
    },
    onToggle: () => {
      // Add any custom toggle behavior here
    },
  };

  public filteredOptions = computed(() => {
    const normalize = (s: string) =>
      s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');

    const keywords = this._searchTerm()
      .split(/[\s,]+/)
      .map(normalize)
      .filter((k) => k.length > 0);

    let sections: DropdownSection<T>[] = [];

    if (this.type() === 'default') {
      sections = this.sections();
    } else {
      this.sections().forEach((section) => {
        const score = (option: DropdownOption<T>): number => {
          const parts = normalize(option.label)
            .split(',')
            .map((p) => p.trim());
          const matchScore = keywords.reduce((total, kw) => {
            const idx = parts.findIndex((p) => p.includes(kw));
            return total + (idx === -1 ? parts.length : idx);
          }, 0);
          return matchScore * 100 + parts.length;
        };

        let filteredOptions = section.options.filter((option) => {
          if (keywords.length === 0) return true;
          const label = normalize(option.label);
          return keywords.every((kw) => label.includes(kw));
        });

        if (filteredOptions.length > 5) {
          filteredOptions = filteredOptions.sort((a, b) => score(a) - score(b));
        }

        if (filteredOptions.length > 0) {
          sections.push({ title: section.title, options: filteredOptions });
        }
      });
    }

    return sections;
  });

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    effect(() => {
      if (this.isDisabled()) {
        this.dropdown?.destroy();
      } else {
        if (
          isPlatformBrowser(this.platformId) &&
          this.dropdownTargetRef() &&
          this.dropdownTrigger()
        ) {
          this.dropdown = new FlowbiteDropdown(
            this.dropdownTargetRef()?.nativeElement,
            this.dropdownTrigger()?.nativeElement,
            {
              ...this.flowbiteOptions,
              triggerType: this.type() === 'search-bar' ? 'none' : 'click',
            },
          );

          this.dropdown?.hide();
        }
      }
    });
  }

  ngOnInit(): void {
    if (this.formGroup() && this.controlName()) {
      const control = this.formGroup().get(this.controlName());
      if (control) {
        const controlValue = control.value;
        if (controlValue != null) {
          this.initializeSelectedOption(controlValue);
        }

        const syncDisabled = () => {
          this.controlDisabled.set(control.disabled);
          if (control.disabled) {
            this.dropdownForm.controls.dropdownFormControl.disable({ emitEvent: false });
          } else {
            this.dropdownForm.controls.dropdownFormControl.enable({ emitEvent: false });
          }
        };

        syncDisabled();
        control.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => syncDisabled());
      }
    }

    this.dropdownForm.controls.dropdownFormControl.setValue(this.selected()?.label ?? null);

    this.dropdownForm.controls.dropdownFormControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (typeof value === 'string') {
          if (this.selected() && value !== this.selected()!.label) {
            this.selected.set(null);
            const control = this.formGroup()?.get(this.controlName());
            if (control) {
              control.setValue(null);
              control.markAsDirty();
              control.markAsTouched();
            }
          }
          this._searchTerm.set(value);
          if (this.type() === 'search-bar') {
            if (value.trim().length >= 3) {
              if (!this.dropdownExpanded()) this.dropdown?.show();
            } else {
              if (this.dropdownExpanded()) this.dropdown?.hide();
            }
          }
        } else if (value == null) {
          this._searchTerm.set('');
          if (this.type() === 'search-bar' && this.dropdownExpanded()) {
            this.dropdown?.hide();
          }
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSelectedOption(value: any) {
    // Check in all sections
    for (const section of this.sections()) {
      const found = section.options.find((o) => o.value === value);
      if (found != null) {
        this.selected.set(found);
        return;
      }
    }
  }

  public onSelect(option: DropdownOption<T>): void {
    this.selected.set(option);
    this._searchTerm.set('');
    this.dropdownForm.controls.dropdownFormControl.setValue(option.label, { emitEvent: false });
    this.dropdownForm.controls.dropdownFormControl.markAsDirty();
    this.dropdownForm.controls.dropdownFormControl.markAsTouched();

    // Update form control
    if (this.formGroup() && this.controlName()) {
      const control = this.formGroup().get(this.controlName());
      if (control) {
        control.setValue(option.value); // value for parent form
        control.markAsDirty();
        control.markAsTouched();
      }
    }

    this.dropdown?.hide();
    this.select.emit(option);
  }

  public isOptionSelected(option: DropdownOption<T>): boolean {
    return this.selected()?.id === option.id;
  }

  public clearSelection(): void {
    this.selected.set(null);
    this._searchTerm.set('');
    this.dropdownForm.controls.dropdownFormControl.setValue(null, { emitEvent: false });
    const control = this.formGroup()?.get(this.controlName());
    if (control) {
      control.setValue(null);
      control.markAsDirty();
      control.markAsTouched();
    }
  }

  public dropdownMenuClasses = computed(() => {
    const classes: string[] = [];

    if (!this.isDisabled()) {
      classes.push(
        'border',
        'border-grey-50',
        'absolute',
        'w-full',
        'top-0',
        '!transform-none',
        'z-0',
        'bg-white',
        'w-full',
        'rounded-md',
        'shadow-[0px_32px_48px_-8px_rgba(0,0,0,0.1)]',
      );
      if (this.size() === 'small') {
        classes.push('pt-10', 'px-3xs', 'pb-3xs');
      } else if (this.size() === 'medium') {
        classes.push('pt-14', 'px-2xs', 'pb-2xs');
      } else if (this.size() === 'large') {
        classes.push('pt-[66px]', 'px-xs', 'pb-xs');
      }
    }

    return classes.join(' ');
  });

  public dropdownTriggerClasses = computed(() => {
    const classes: string[] = ['relative', 'z-[10]', 'w-full'];

    if (this.size() === 'small') {
      classes.push('p-3xs');
    } else if (this.size() === 'medium') {
      classes.push('p-2xs');
    } else if (this.size() === 'large') {
      classes.push('p-xs');
    }

    return classes.join(' ');
  });
}
